import React, { useEffect, useRef, useState } from "react";
import { Radio, Play, Pause, Sparkles } from "lucide-react";
import api from "../lib/api";

/**
 * KidsRadio — six curated kid-safe audio stations.
 *
 * Uses a single <audio> element so play/pause is always in-sync. Tapping
 * a tile plays that station; tapping the same tile again pauses. All
 * streams are CC0 / royalty-free tracks from cloudfront/pixabay.
 */
export default function KidsRadio() {
  const [stations, setStations] = useState([]);
  const [active, setActive] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    api.get("/kids-public/radio").then((r) => setStations(r.data?.stations || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!audioRef.current) return;
    if (active) {
      audioRef.current.src = active.stream_url;
      audioRef.current.play().catch(() => {});
    } else {
      audioRef.current.pause();
    }
  }, [active]);

  const toggle = (s) => setActive((cur) => cur?.id === s.id ? null : s);

  return (
    <div data-testid="kids-radio-page" className="pb-20 px-3 sm:px-6 md:px-10 max-w-5xl mx-auto text-white">
      <div className="relative overflow-hidden rounded-3xl p-5 sm:p-8 mt-3 bg-gradient-to-br from-violet-500 via-fuchsia-500 to-rose-500 shadow-2xl ring-4 ring-white/25">
        <div className="absolute -top-8 -right-8 opacity-25 pointer-events-none text-[10rem]">📻</div>
        <div className="relative flex items-center gap-3 flex-wrap">
          <img src="/kids-learn-play-logo.png" alt="" className="w-20 sm:w-28 h-auto drop-shadow-2xl" />
          <div>
            <div className="inline-flex items-center gap-1 bg-white/20 text-white text-[10px] uppercase tracking-[0.25em] font-black px-2 py-0.5 rounded-full backdrop-blur"><Sparkles className="w-3 h-3" /> Kid Radio</div>
            <h1 className="font-display font-black text-3xl md:text-5xl tracking-tighter drop-shadow mt-1">Tune in!</h1>
            <p className="text-white/90 text-sm">Six safe stations to play anytime.</p>
          </div>
        </div>
      </div>

      <audio ref={audioRef} loop />

      <div data-testid="radio-grid" className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {stations.map((s) => {
          const playing = active?.id === s.id;
          return (
            <button
              key={s.id}
              data-testid={`radio-station-${s.id}`}
              onClick={() => toggle(s)}
              className={`relative overflow-hidden rounded-2xl p-5 text-left ring-4 ${playing ? "ring-yellow-300" : "ring-white/25"} bg-gradient-to-br ${s.color} shadow-xl hover:scale-[1.02] transition`}
            >
              <div className="flex items-center gap-3">
                <div className="text-6xl drop-shadow-lg">{s.emoji}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-black text-xl text-white">{s.title}</div>
                  <div className="text-white/85 text-xs">{s.tagline}</div>
                </div>
                <div className="w-12 h-12 rounded-full bg-white/90 text-[#222] flex items-center justify-center shadow-lg">
                  {playing ? <Pause className="w-6 h-6 fill-[#222]" /> : <Play className="w-6 h-6 ml-0.5 fill-[#222]" />}
                </div>
              </div>
              {playing && (
                <div data-testid={`radio-playing-${s.id}`} className="mt-3 flex items-center gap-2 text-xs text-white font-bold bg-black/25 rounded-full px-3 py-1 w-fit">
                  <span className="flex gap-0.5 items-end h-3">
                    <span className="w-1 bg-white animate-[bounce_0.8s_infinite]" style={{ height: "50%" }} />
                    <span className="w-1 bg-white animate-[bounce_0.8s_infinite_0.2s]" style={{ height: "85%" }} />
                    <span className="w-1 bg-white animate-[bounce_0.8s_infinite_0.4s]" style={{ height: "65%" }} />
                  </span>
                  On air
                </div>
              )}
            </button>
          );
        })}
      </div>

      {stations.length === 0 && <div className="mt-8 text-white/60 text-center" data-testid="radio-empty">Loading stations…</div>}
    </div>
  );
}
